import { format } from 'date-fns';
import * as React from 'react';
import { cpfMask } from '../../utils/formatters/cpfMask';
import { phoneMask } from '../../utils/formatters/phoneMask';
import { useMessage } from '../messages/useMessage';
import { useInterviewService } from '../services/interview-service';

const RESOURCE_NOTFOUND = 'api.resource-notfound';

export const useInterviewController = () => {
  const DATE_FORMAT = 'dd/MM/yyyy';
  const {
    save,
    fetchData,
    data,
    error,
    findActiveInterviewByCpf,
    findValidatePreRegistration,
    findLastInterviewByCpfAndModule,
    findInterviewById,
    getFilters,
    updateConditionsUse,
    updateBasicData,
    getNotasRespostas,
    getRespostaUser,
    getListInterviewOfCandidate,
    addHiglight,
    removeHiglight,
    generateCertificate,
    getUrlCertificate,
    certificateDate,
    loading,
    sendInterviewCertificate,
    getUrlDownloadCertificate,
    verifyHasAnyFilterActive,
    getInterviewByuserAuth,
    findInterviewByAdm,
    findInterviewByIdPublic,
    validateNewInterviewByExpiredStatus,
    serviceMigrateInterview,
    getRegistrationDataBoard,
    extendInterview,
  } = useInterviewService();
  const [loadingController, setLoadingController] = React.useState(false);
  const { handleError, handleCustomSuccess, handleSuccess } = useMessage();

  const postSendInterviewCertificate = React.useCallback(
    (data) => {
      setLoadingController(true);
      return sendInterviewCertificate(data)
        .then((data) => {
          handleCustomSuccess(
            'Certificado digital enviado!',
            'Envio do certificado digital do candidato realizado com sucesso.',
          );
        })
        .catch((error) => {
          handleError(error.message);
        })
        .finally(() => setLoadingController(false));
    },
    [handleCustomSuccess, handleError, sendInterviewCertificate],
  );

  const putExtendInterview = React.useCallback(
    (idInterview) => {
      setLoadingController(true);
      return extendInterview(idInterview)
        .then(() => {
          handleSuccess('Prazo prorrogado com sucesso.');
        })
        .catch((error) => {
          handleError(error.message ?? 'Erro ao prorrogar prazo.');
        })
        .finally(() => setLoadingController(false));
    },
    [extendInterview, handleError, handleSuccess],
  );

  const getActiveInterviewByCpf = React.useCallback(
    (cpf) => {
      setLoadingController(true);
      return findActiveInterviewByCpf(cpf)
        .then((data) => data?.[0])
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar a entrevista por CPF');
        })
        .finally(() => setLoadingController(false));
    },
    [findActiveInterviewByCpf, handleError],
  );

  const getValidatePreRegistration = React.useCallback(
    (moduleId, cpf) => {
      setLoadingController(true);
      return findValidatePreRegistration(moduleId, cpf)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar a entrevista por CPF');
        })
        .finally(() => setLoadingController(false));
    },
    [findValidatePreRegistration, handleError],
  );

  const postMigrateInterviews = React.useCallback(
    (data) => {
      setLoadingController(true);
      return serviceMigrateInterview(data)
        .catch((error) => {
          console.error(error);
          handleError(error?.message);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [serviceMigrateInterview, handleError],
  );

  const getValidateNewInterviewByExpiredStatus = React.useCallback(
    (id, moduleId, cpf) => {
      setLoadingController(true);
      return validateNewInterviewByExpiredStatus(id, moduleId, cpf)
        .catch((error) => {
          handleError(
            'Erro ao validar criação de nova entrevista com status EXPIRADA.',
          );
        })
        .finally(() => setLoadingController(false));
    },
    [handleError, validateNewInterviewByExpiredStatus],
  );

  const getDownloadCertificate = React.useCallback(
    (id) => {
      setLoadingController(true);
      return getUrlDownloadCertificate(id)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar fazer o download do certificado.');
        })
        .finally(() => setLoadingController(false));
    },
    [getUrlDownloadCertificate, handleError],
  );

  const getInterviewDataForBoard = React.useCallback(
    (id) => {
      setLoadingController(true);
      return getRegistrationDataBoard(id)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError(
            'Erro ao tentar buscar o detalhe dos dados da entrevista.',
          );
        })
        .finally(() => setLoadingController(false));
    },
    [getRegistrationDataBoard, handleError],
  );

  const getCertificatedate = React.useCallback(
    (module, cpf) => {
      setLoadingController(true);
      return certificateDate(module, cpf)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError(error[0]?.message);
        })
        .finally(() => setLoadingController(false));
    },
    [certificateDate, handleError],
  );

  const getLastIntervewByCpfAndModule = React.useCallback(
    (cpf, module) => {
      setLoadingController(true);
      return findLastInterviewByCpfAndModule(cpf, module)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar a entrevista por CPF');
        })
        .finally(() => setLoadingController(false));
    },
    [findLastInterviewByCpfAndModule, handleError],
  );

  const buscarUrlCertificado = React.useCallback(
    (id) => {
      setLoadingController(true);
      return getUrlCertificate(id)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar o link do certificado.');
        })
        .finally(() => setLoadingController(false));
    },
    [getUrlCertificate, handleError],
  );

  const buscarListaDeEntrevistas = React.useCallback(
    (idModule, id) => {
      setLoadingController(true);
      return getListInterviewOfCandidate(idModule, id)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar lista de entrevistas.');
        })
        .finally(() => setLoadingController(false));
    },
    [getListInterviewOfCandidate, handleError],
  );

  const buscarNotasRespostas = React.useCallback(
    (id) => {
      setLoadingController(true);
      return getNotasRespostas(id)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar notas');
        })
        .finally(() => setLoadingController(false));
    },
    [getNotasRespostas, handleError],
  );
  const buscarRespostas = React.useCallback(
    (id) => {
      setLoadingController(true);
      return getRespostaUser(id)
        .then((data) => data)
        .catch((error) => {
          console.error(error);
          handleError('Erro ao tentar buscar notas');
        })
        .finally(() => setLoadingController(false));
    },
    [getRespostaUser, handleError],
  );

  const saveInterview = React.useCallback(
    (interview) => {
      setLoadingController(true);
      return save(interview)
        .then((data) => {
          setLoadingController(false);
          return data;
        })
        .catch((error) => {
          handleError(error.message ?? 'Erro ao tentar salvar entrevista.');
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [handleError, save],
  );

  const gerarCertificado = React.useCallback(
    (data) => {
      setLoadingController(true);
      return generateCertificate(data)
        .then((response) => {
          setLoadingController(false);
          return response;
        })
        .catch((error) => {
          handleError(error?.message);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [generateCertificate, handleError],
  );

  const sendUpdateBasicData = React.useCallback(
    (interview) => {
      setLoadingController(true);
      return updateBasicData(interview)
        .then((data) => {
          setLoadingController(false);
          return data;
        })
        .catch((error) => {
          console.error(error);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [updateBasicData],
  );

  const addHiglightInterview = React.useCallback(
    (id) => {
      setLoadingController(true);
      return addHiglight(id)
        .then((data) => {
          setLoadingController(false);
          return data;
        })
        .catch((error) => {
          console.error(error);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [addHiglight],
  );

  const getHasAnyFilterActive = React.useCallback(() => {
    setLoadingController(true);
    return verifyHasAnyFilterActive()
      .then((data) => {
        setLoadingController(false);
        return data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => setLoadingController(false));
  }, [verifyHasAnyFilterActive]);

  const removeHiglightInterview = React.useCallback(
    (id) => {
      setLoadingController(true);
      return removeHiglight(id)
        .then((data) => {
          setLoadingController(false);
          return data;
        })
        .catch((error) => {
          console.error(error);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [removeHiglight],
  );

  const getDates = React.useCallback((email, labelSend, labelOpen) => {
    const sendEmailDate = new Date(email.sendEmailDate);

    const dates = {
      sendEmailDate: `${labelSend}${format(
        sendEmailDate.setDate(sendEmailDate.getDate()),
        DATE_FORMAT,
      )}`,
    };

    if (email.openedEmailDate) {
      const openedEmailDate = new Date(email.openedEmailDate);
      return {
        ...dates,
        openedEmailDate: `${labelOpen}${format(
          openedEmailDate.setDate(openedEmailDate.getDate()),
          DATE_FORMAT,
        )}`,
      };
    } else {
      return { ...dates, openedEmailDate: 'Não aberto' };
    }
  }, []);

  const getInterviewEmailDates = React.useCallback(
    (interview) => {
      let emailDates = {};
      let historyEmailDates = [];
      let showUnfilledUnfinishedMark = false;
      let messageUnfilledUnfinished = '';

      interview.sendingRegistrationEmail.forEach((email, index) => {
        if (index === 0) {
          emailDates = getDates(email, 'Enviado: ', 'Aberto: ');
          showUnfilledUnfinishedMark = email.showUnfilledUnfinishedMark;
          messageUnfilledUnfinished = email.messageUnfilledUnfinished;
        } else {
          historyEmailDates = [...historyEmailDates, getDates(email, '', '')];
        }
      });

      return {
        emailDates,
        historyEmailDates,
        showUnfilledUnfinishedMark,
        messageUnfilledUnfinished,
      };
    },
    [getDates],
  );

  const findInterviewByUserAuth = React.useCallback(() => {
    setLoadingController(true);
    return getInterviewByuserAuth()
      .then((data) => data)
      .catch((error) => {
        if (error.errorCode === RESOURCE_NOTFOUND) {
          return null;
        }
        throw error;
      })
      .finally(() => setLoadingController(false));
  }, [getInterviewByuserAuth]);

  const findInterviewByAdministratorAuth = React.useCallback(
    (id) => {
      setLoadingController(true);
      return findInterviewByAdm(id)
        .then((data) => data)
        .catch((error) => {
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [findInterviewByAdm],
  );

  const getPersonalData = React.useCallback((interview) => {
    return `${interview.candidate.name} \n ${cpfMask(
      interview.candidate.cpf,
    )} / ${phoneMask(interview.phone)} \n ${interview.email}`;
  }, []);

  const getStatusCompletingInterview = React.useCallback((interview) => {
    const statusCompletingInterviewMap = {
      NAO_PREENCHIDO: 'Não preenchido',
      EM_PREENCHIMENTO: 'Em preenchimento',
      FINALIZADA: 'Finalizada',
      EXPIRADA: 'Expirada',
      REENTREVISTA: 'Reentrevista',
      EM_REVISAO: 'Em revisão',
      CERTIFICADA: 'Certificada',
      DESISTENTE: 'Desistente',
      AGUARDANDO_AVALIACAO: 'Aguardando avaliação',
    };

    return (
      statusCompletingInterviewMap[interview.statusCompletingInterview] ??
      interview.statusCompletingInterview
    );

    // return (
    //   statusByModule[interview.module.id][
    //     interview.statusCompletingInterview
    //   ] ?? interview.statusCompletingInterview
    // );
  }, []);

  const getReverseStatusCompletingInterviews = React.useCallback((items) => {
    const statusCompletingInterviewMap = {
      Finalizada: 'FINALIZADA',
      'Em preenchimento': 'EM_PREENCHIMENTO',
      'Não preenchido': 'NAO_PREENCHIDO',
      Expirada: 'EXPIRADA',
      Reentrevista: 'REENTREVISTA',
    };
    return items.map((item) => statusCompletingInterviewMap[item]);
  }, []);

  const getStatusInterview = React.useCallback((interview) => {
    const statusInterviewMap = {
      APTO: 'Apto',
      NAO_APTO: 'Não apto',
      SEM_RESULTADO: 'Sem resultado',
      ERRO_DE_PROCESSAMENTO: 'Erro de processamento',
      EM_PROCESSAMENTO: 'Em processamento',
      AGUARDANDO_AVALIACAO: 'Aguardando avaliação',
      CONCLUIDO: 'Concluído',
    };
    return (
      statusInterviewMap[interview?.statusInterview] ??
      interview.statusInterview
    );
  }, []);

  const getReverseStatusInterviews = React.useCallback((items) => {
    const statusInterviewMap = {
      Apto: 'APTO',
      'Não apto': 'NAO_APTO',
      'Erro de processamento': 'ERRO_DE_PROCESSAMENTO',
      'Sem resultado': 'SEM_RESULTADO',
    };
    return items.map((item) => statusInterviewMap[item]);
  }, []);

  const getStatusEmail = React.useCallback((status) => {
    const statusEmailMap = {
      ENVIADO: 'Enviado',
      NAO_ENVIADO: 'Não enviado',
      ABERTO: 'Aberto',
      NAO_ABERTO: 'Não aberto',
    };
    return statusEmailMap[status];
  }, []);

  const getReverseStatusEmails = React.useCallback((items) => {
    const statusEmailMap = {
      Enviado: 'ENVIADO',
      'Não enviado': 'NAO_ENVIADO',
      Aberto: 'ABERTO',
      'Não aberto': 'NAO_ABERTO',
    };
    return items.map((item) => statusEmailMap[item]);
  }, []);

  const getFilterValues = React.useCallback(
    (items, key) => {
      const filterMap = {
        UF: items,
        Cidade: items,
        Email: getReverseStatusEmails(items),
        Status: getReverseStatusCompletingInterviews(items),
        Resultado: getReverseStatusInterviews(items),
      };
      return filterMap[key];
    },
    [
      getReverseStatusCompletingInterviews,
      getReverseStatusEmails,
      getReverseStatusInterviews,
    ],
  );

  const getFiltersByType = React.useCallback(
    async (moduleId, filter) => {
      try {
        if (filter.filterType === 'STATUS_EMAIL') {
          return [
            { label: 'ABERTO' },
            { label: 'ENVIADO' },
            { label: 'NAO_ABERTO' },
            { label: 'NAO_ENVIADO' },
          ];
        }

        if (!moduleId) {
          return;
        }

        const data = await getFilters(moduleId, filter);

        const filterMapper = {
          UF: data?.map((uf) => ({
            id: uf.id,
            label: uf.initials,
          })),
          CITY: data?.map((city) => ({
            id: city.id,
            label: city.name,
          })),
          STATUS_INTERVIEW: data?.map((status) => ({
            label: status,
          })),
          STATUS_RESULT: data?.map((status) => ({
            label: status,
          })),
        };
        return filter.filterType === 'STATUS_COMBINATIONS'
          ? data
          : filterMapper[filter.filterType];
      } catch (error) {
        console.error(error);
        handleError('Erro ao tentar carregar os filtros da coluna selecionada');
      }
    },
    [handleError, getFilters],
  );

  const setConditionalUse = React.useCallback(
    (id, conditions) => {
      setLoadingController(true);
      return updateConditionsUse(id, conditions)
        .then((data) => {
          setLoadingController(false);
          return data;
        })
        .catch((error) => {
          console.error(error);
          throw error;
        })
        .finally(() => setLoadingController(false));
    },
    [updateConditionsUse],
  );

  return {
    getDates,
    getStatusCompletingInterview,
    getInterviewEmailDates,
    getStatusInterview,
    getPersonalData,
    saveInterview,
    getActiveInterviewByCpf,
    getValidatePreRegistration,
    getLastIntervewByCpfAndModule,
    findInterviewById,
    fetchData,
    getFiltersByType,
    getStatusEmail,
    setConditionalUse,
    sendUpdateBasicData,
    getFilterValues,
    data,
    error,
    loading,
    buscarNotasRespostas,
    buscarRespostas,
    buscarListaDeEntrevistas,
    addHiglightInterview,
    removeHiglightInterview,
    gerarCertificado,
    buscarUrlCertificado,
    getCertificatedate,
    loadingController,
    postSendInterviewCertificate,
    getDownloadCertificate,
    getHasAnyFilterActive,
    findInterviewByUserAuth,
    findInterviewByAdministratorAuth,
    findInterviewByIdPublic,
    getValidateNewInterviewByExpiredStatus,
    postMigrateInterviews,
    getInterviewDataForBoard,
    putExtendInterview,
  };
};
